// useCamera.js
import * as THREE from 'three';
import * as TWEEN from '@tweenjs/tween.js';

/**
 * Tweens the camera position and OrbitControls target to new coords.
 * 
 * @param {THREE.Camera} camera - The camera to animate
 * @param {THREE.OrbitControls} controls - The orbit controls
 * @param {THREE.Vector3} newPosition - Target position for the camera
 * @param {THREE.Vector3} newControlsTarget - Target position for the controls
 * @param {number} duration - Duration of the animation in milliseconds
 */
export function animateCamera(camera, controls, newPosition, newControlsTarget, duration = 1000) {
  new TWEEN.Tween(camera.position)
    .to({ x: newPosition.x, y: newPosition.y, z: newPosition.z }, duration)
    .easing(TWEEN.Easing.Quadratic.Out)
    .start();

  new TWEEN.Tween(controls.target)
    .to({ x: newControlsTarget.x, y: newControlsTarget.y, z: newControlsTarget.z }, duration)
    .easing(TWEEN.Easing.Quadratic.Out)
    .onUpdate(() => controls.update())
    .start();
}

/**
 * Prevents camera from going inside the model(s).
 * 
 * @param {Object} options - Options object
 * @param {THREE.Camera} options.camera - The camera to prevent penetration for
 * @param {THREE.OrbitControls} options.controls - The orbit controls
 * @param {THREE.Object3D} options.bodyModel - The body model to prevent penetration into
 * @param {Object} options.productModels - Product models to prevent penetration into
 * @param {Object} options.recursionFlagRef - Reference to recursion prevention flag
 */
export function preventCameraPenetration({
  camera,
  controls,
  bodyModel,
  productModels,
  recursionFlagRef
}) {
  if (!controls.enabled || recursionFlagRef.value) return;
  recursionFlagRef.value = true;

  const direction = new THREE.Vector3().subVectors(camera.position, controls.target);
  const distance = direction.length();
  direction.normalize();

  // If near minDistance, skip
  if (distance <= controls.minDistance + 0.1) {
    recursionFlagRef.value = false;
    return;
  }

  // Need a bounding sphere to see if we're inside the body
  if (!bodyModel) {
    recursionFlagRef.value = false;
    return;
  }
  if (!bodyModel._boundingSphere) {
    const box = new THREE.Box3().setFromObject(bodyModel);
    const sphere = new THREE.Sphere();
    box.getBoundingSphere(sphere);
    bodyModel._boundingSphere = sphere;
  }
  const boundingSphere = bodyModel._boundingSphere.clone();

  const distCamToTarget = controls.target.distanceTo(camera.position);
  const distCenterToTarget = controls.target.distanceTo(boundingSphere.center);

  // If we're definitely outside bounding sphere, skip
  if (distCamToTarget > distCenterToTarget + boundingSphere.radius) {
    recursionFlagRef.value = false;
    return;
  }

  const rayOrigin = new THREE.Vector3().copy(controls.target).add(direction.multiplyScalar(0.05));
  const raycaster = new THREE.Raycaster(rayOrigin, direction);

  // Gather meshes
  const meshes = [];
  bodyModel.traverse((child) => { if (child.isMesh) meshes.push(child); });
  Object.values(productModels).forEach((model) => {
    model.traverse((child) => { if (child.isMesh) meshes.push(child); });
  });

  const intersections = raycaster.intersectObjects(meshes, false);
  if (intersections.length > 0) {
    const valid = intersections.filter(i => i.distance > 0);
    if (valid.length > 0) {
      const hitDistance = valid[0].distance;
      const cameraDistance = distCamToTarget - 0.05;
      if (hitDistance < cameraDistance) {
        const diff = cameraDistance - hitDistance;
        if (diff > 0.1) {
          // Push camera behind intersection
          const safetyMargin = 0.3;
          const newPos = new THREE.Vector3()
            .copy(direction)
            .multiplyScalar(hitDistance + 0.05 - safetyMargin)
            .add(controls.target);

          const up = camera.up.clone();
          camera.position.copy(newPos);
          camera.up.copy(up);
          controls.update();
        }
      }
    }
  }

  recursionFlagRef.value = false;
}